<template>
  <div class="lead-nested-filters-wrapper p-ml-2">
    <template v-if="isLeadPlan">
      <Button
        :label="nestedPlansButtonLabel"
        class="p-button-outlined"
        @click="(event) => $refs.nestedPlansOverlay.toggle(event)"
      />
      <OverlayPanel ref="nestedPlansOverlay" appendTo="body" class="nested-plans-overlay">
        <div class="nested-plans-panel">
          <div class="nested-plans-header p-d-flex p-jc-between p-ai-center p-mb-3">
            <h3 class="p-m-0">Nested Plans</h3>
            <Button
              icon="pi pi-times"
              class="p-button-rounded p-button-text p-button-sm"
              @click="$refs.nestedPlansOverlay.hide()"
            />
          </div>
          <div
            v-if="currentNestedPlans.length >= 2"
            class="p-d-flex p-jc-end p-mb-3"
          >
            <Button
              :label="toggleAllButtonLabel"
              class="p-button-text p-button-sm"
              @click="onToggleAllClick"
            />
          </div>
          <div
            v-for="(plan, index) of currentNestedPlans"
            :key="plan.key"
            class="p-field-checkbox p-d-flex p-jc-between p-mb-2"
          >
            <label :for="'nested_plan_' + index">{{ getDisplayNameForPlan(plan) }}</label>
            <InputSwitch
              :id="'nested_plan_' + index"
              v-model="visibleNestedPlansObject[plan.id.intID]"
              @input="onInputSwitchInput($event, plan.id.intID)"
              class="p-ml-4"
            />
          </div>
        </div>
      </OverlayPanel>
    </template>
    <template v-else>
      <Button
        :label="leadPlanButtonLabel"
        class="p-button-outlined"
        :class="{ 'is-active': isLeadPlanVisible }"
        @click="isLeadPlanVisible = !isLeadPlanVisible"
      />
    </template>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import InputSwitch from 'primevue/inputswitch'
import Button from 'primevue/button'
import OverlayPanel from 'primevue/overlaypanel'
import Plan from '@/models/Plan'

Vue.component('InputSwitch', InputSwitch)
Vue.component('Button', Button)
Vue.component('OverlayPanel', OverlayPanel)

export default Vue.extend({
  name: 'LeadNestedFilters',
  computed: {
    currentPlan(): Plan {
      return this.$store.getters.currentPlan
    },
    currentNestedPlans(): Plan[] {
      return this.$store.getters.allPlans
        .filter((plan: Plan) =>
          this.currentPlan.plans.find((planId) => planId.intID == plan.id.intID)
            ? true
            : false
        )
        .sort((a: Plan, b: Plan) => a.orderIndex - b.orderIndex)
    },
    isLeadPlan(): boolean {
      return this.$store.getters.currentPlan.plans.length ? true : false
    },
    nestedPlansButtonLabel(): string {
      return `Nested Plans ${this.visibleNestedPlanIntIds.length} / ${this.currentNestedPlans.length}`
    },
    leadPlanButtonLabel(): string {
      return this.isLeadPlanVisible ? `Lead Plan ON` : `Lead Plan OFF`
    },
    currentLeadPlanFilterKey(): string {
      return 'lpl-' + this.$store.getters.currentPlanIntID.toString()
    },
    currentNestedPlansFilterKey(): string {
      return 'npl-' + this.$store.getters.currentPlanIntID.toString()
    },
    isLeadPlanVisible: {
      get(): boolean {
        return this.$store.getters.isLeadPlanVisible
      },
      set(newValue: boolean) {
        this.$store.commit('updateIsLeadPlanVisible', newValue)
        window.localStorage.setItem(
          this.currentLeadPlanFilterKey,
          this.$store.getters.isLeadPlanVisibleQueryString
        )
      },
    },
    visibleNestedPlanIntIds: {
      get(): number[] {
        return this.$store.getters.visibleNestedPlansIntIds
      },
      set(newValue: number[]) {
        if (newValue.length == 0) {
          this.$store.commit('updateVisibleNestedPlansIntIds', [])
          window.localStorage.setItem(
            this.currentNestedPlansFilterKey,
            ''
          )
          this.$store.commit('isNestedPlanFilterActive', false)
        } else {
          this.$store.commit('updateVisibleNestedPlansIntIds', newValue)
          window.localStorage.setItem(
            this.currentNestedPlansFilterKey,
            this.$store.getters.visibleNestedPlansQueryString
          )
          this.$store.commit('isNestedPlanFilterActive', true)
        }
      },
    },
    visibleNestedPlansObject(): Record<number, boolean> {
      const returnObj: Record<number, boolean> = {}
      this.currentNestedPlans.forEach((plan) => {
        returnObj[plan.id.intID] = this.visibleNestedPlanIntIds?.includes(
          plan.id.intID
        )
      })
      return returnObj
    },
    areAllNestedPlansVisible(): boolean {
      return this.visibleNestedPlanIntIds.length === this.currentNestedPlans.length
    },
    toggleAllButtonLabel(): string {
      return this.areAllNestedPlansVisible ? 'Hide All' : 'Show All'
    },
  },
  methods: {
    getDisplayNameForPlan(plan: Plan): string {
      return plan.abbreviatedName === '' ? plan.name : `${plan.abbreviatedName + ' '} | ${' ' + plan.name}`
    },
    onToggleAllClick() {
      if (this.areAllNestedPlansVisible) {
        this.visibleNestedPlanIntIds = []
      } else {
        this.visibleNestedPlanIntIds = this.currentNestedPlans.map((plan: Plan) => plan.id.intID)
      }
      this.$forceUpdate()
    },
    onInputSwitchInput(value: boolean, planIntId: number) {
      if (value) {
        this.visibleNestedPlanIntIds = [
          ...this.visibleNestedPlanIntIds,
          planIntId,
        ]
      } else {
        this.visibleNestedPlanIntIds = this.visibleNestedPlanIntIds.filter(
          (existingPlanIntId) => existingPlanIntId !== planIntId
        )
      }
      this.$forceUpdate()
    },
  },
})
</script>

<style lang="scss">
@import '@/styles/_imports.scss';

.nested-plans-overlay {
  .nested-plans-panel {
    min-width: 22rem;
  }
  .p-inputswitch {
    transform: scale(0.75);

    &.p-inputswitch-checked {
      .p-inputswitch-slider {
        background-color: $tacticColorPR;
      }
    }
    &.p-inputswitch-checked:not(.p-disabled):hover {
      .p-inputswitch-slider {
        background-color: $tacticColorPRBkg;
      }
    }
  }
}
</style>
